import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  HouseLine, Lightbulb, Fan, Thermometer, ToggleLeft, ToggleRight,
  Plug, Broadcast, Plus, Trash, Play, CaretRight, Warning,
  SlidersHorizontal, X, GearSix, Sun, Moon, PaperPlaneRight, ChatCircleDots
} from '@phosphor-icons/react';

const DOMAIN_CONFIG = {
  light: { icon: Lightbulb, color: '#F59E0B', label: 'Lights' },
  switch: { icon: Plug, color: '#22C55E', label: 'Switches' },
  fan: { icon: Fan, color: '#3B82F6', label: 'Fans' },
  climate: { icon: Thermometer, color: '#FF6B35', label: 'Climate' },
  cover: { icon: SlidersHorizontal, color: '#A855F7', label: 'Covers' },
  sensor: { icon: Broadcast, color: '#00E5FF', label: 'Sensors' },
  binary_sensor: { icon: Broadcast, color: '#00E5FF', label: 'Sensors' },
  media_player: { icon: Broadcast, color: '#EC4899', label: 'Media' },
  input_boolean: { icon: ToggleLeft, color: '#22C55E', label: 'Toggles' },
  automation: { icon: GearSix, color: '#737373', label: 'Automations' },
  scene: { icon: Sun, color: '#F59E0B', label: 'Scenes' },
};

const DOMAIN_TABS = ['all', 'light', 'switch', 'fan', 'climate', 'sensor'];

export default function SmartHomePage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [showSceneForm, setShowSceneForm] = useState(false);
  const [sceneForm, setSceneForm] = useState({ name: '', icon: 'HouseLine', actions: [], notes: '' });
  const [controlDevice, setControlDevice] = useState(null);
  const [brightness, setBrightness] = useState(128);
  const [temperature, setTemperature] = useState(24);
  const [nlCommand, setNlCommand] = useState('');
  const [nlResult, setNlResult] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/iot/status');
      setStatus(res.data);
      if (res.data.connected) {
        const [devRes, scnRes] = await Promise.all([
          api.get('/iot/devices'),
          api.get('/iot/scenes')
        ]);
        setDevices(devRes.data);
        setScenes(scnRes.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleToggle = async (entityId) => {
    try {
      await api.post('/iot/toggle', { entity_id: entityId });
      // Optimistic update
      setDevices(prev => prev.map(d =>
        d.entity_id === entityId
          ? { ...d, state: d.state === 'on' ? 'off' : 'on' }
          : d
      ));
      // Refresh after a short delay
      setTimeout(fetchStatus, 1500);
    } catch (e) { console.error(e); }
  };

  const handleAction = async (entityId, service, data = {}) => {
    try {
      await api.post('/iot/action', { entity_id: entityId, service, data });
      setTimeout(fetchStatus, 1500);
    } catch (e) { console.error(e); }
  };

  const executeScene = async (sceneId) => {
    try {
      await api.post(`/iot/scenes/${sceneId}/execute`);
      setTimeout(fetchStatus, 2000);
    } catch (e) { console.error(e); }
  };

  const deleteScene = async (sceneId) => {
    try {
      await api.delete(`/iot/scenes/${sceneId}`);
      setScenes(prev => prev.filter(s => s.scene_id !== sceneId));
    } catch (e) { console.error(e); }
  };

  const handleCreateScene = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/iot/scenes', sceneForm);
      setScenes(prev => [...prev, res.data]);
      setShowSceneForm(false);
      setSceneForm({ name: '', icon: 'HouseLine', actions: [], notes: '' });
    } catch (err) { console.error(err); }
  };

  const handleNlCommand = async () => {
    if (!nlCommand.trim()) return;
    setNlLoading(true); setNlResult(null);
    try {
      const res = await api.post('/iot/command', { command: nlCommand });
      setNlResult(res.data);
      if (res.data.success) { setNlCommand(''); setTimeout(fetchStatus, 1500); }
    } catch (e) { setNlResult({ success: false, message: e.response?.data?.detail || 'Failed' }); }
    setNlLoading(false);
  };

  const filtered = tab === 'all' ? devices : devices.filter(d => d.domain === tab);

  // Group by room
  const byRoom = {};
  filtered.forEach(d => {
    const room = d.room || 'Other';
    if (!byRoom[room]) byRoom[room] = [];
    byRoom[room].push(d);
  });

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  // Not configured state
  if (!status?.connected) {
    return (
      <div data-testid="smart-home-page" className="space-y-6 relative">
        <div className="glow-orb-teal -top-20 right-0 z-0" />
        <div className="relative z-10">
          <p className="overline">Automation</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('smart_home')}</h1>
        </div>
        <div className="glass-card p-10 text-center max-w-lg mx-auto relative z-10" data-testid="ha-setup-card">
          <HouseLine size={56} weight="duotone" className="mx-auto text-neutral-500 mb-5" />
          <h2 className="font-heading font-semibold text-white text-xl mb-3">{t('ha_not_configured')}</h2>
          <p className="text-sm text-neutral-400 mb-6">{t('ha_setup_guide')}</p>
          <div className="text-left bg-white/[0.02] rounded-2xl p-5 space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
              <p className="text-sm text-neutral-300">Open your Home Assistant instance and navigate to <span className="text-white font-medium">Profile &gt; Long-Lived Access Tokens</span></p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
              <p className="text-sm text-neutral-300">Create a new token and copy it</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
              <p className="text-sm text-neutral-300">Go to <span className="text-white font-medium">HomeCommand Settings &gt; Smart Home</span> and paste the URL and token</p>
            </div>
          </div>
          {status?.message && status.message !== 'Home Assistant not configured. Go to Settings to add URL and token.' && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 rounded-xl px-4 py-2.5">
              <Warning size={14} /> {status.message}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="smart-home-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />
      <div className="glow-orb-orange top-40 -left-20 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Automation</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('smart_home')}</h1>
          <p className="text-sm text-neutral-500 mt-1">{devices.length} devices connected</p>
        </div>
        <button onClick={() => setShowSceneForm(true)} data-testid="create-scene-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('create_scene')}
        </button>
      </div>

      {/* Natural Language Command Bar */}
      <div className="glass-card p-4 relative z-10" data-testid="nl-command-bar">
        <div className="flex items-center gap-3">
          <ChatCircleDots size={20} weight="duotone" className="text-[#00E5FF] flex-shrink-0" />
          <input type="text" value={nlCommand} onChange={e => setNlCommand(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleNlCommand()}
            placeholder={t('command_placeholder')} data-testid="nl-command-input"
            className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-neutral-500" />
          <button onClick={handleNlCommand} disabled={nlLoading || !nlCommand.trim()} data-testid="nl-command-send"
            className="px-4 py-2 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] text-xs font-semibold hover:bg-[#00E5FF]/20 disabled:opacity-30 flex items-center gap-1 transition-colors">
            {nlLoading ? <div className="w-3.5 h-3.5 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" /> : <PaperPlaneRight size={14} />}
            {t('send_command')}
          </button>
        </div>
        {nlResult && (
          <div className={`mt-2 text-xs px-3 py-2 rounded-xl ${nlResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            {nlResult.success ? `Done: ${nlResult.service} on ${nlResult.entity_id}` : nlResult.message || 'Command failed'}
          </div>
        )}
      </div>

      {/* Scenes */}
      {scenes.length > 0 && (
        <div className="relative z-10">
          <p className="overline mb-3">{t('scenes')}</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {scenes.map(scene => (
              <div key={scene.scene_id} className="glass-card p-4 min-w-[160px] flex-shrink-0 animate-fade-up" data-testid={`scene-${scene.scene_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                    <HouseLine size={18} className="text-[#FF6B35]" weight="duotone" />
                  </div>
                  <button onClick={() => deleteScene(scene.scene_id)} className="p-1 rounded-lg hover:bg-white/5 text-neutral-600 hover:text-red-400 transition-colors">
                    <Trash size={12} />
                  </button>
                </div>
                <p className="text-sm font-semibold text-white mb-1">{scene.name}</p>
                <p className="text-[10px] text-neutral-500 mb-3">{scene.actions?.length || 0} actions</p>
                <button onClick={() => executeScene(scene.scene_id)} data-testid={`exec-scene-${scene.scene_id}`}
                  className="w-full py-2 rounded-full border border-[#FF6B35]/30 text-xs font-medium text-[#FF6B35] hover:bg-[#FF6B35]/10 flex items-center justify-center gap-1 transition-colors">
                  <Play size={12} weight="fill" /> Run
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 w-fit relative z-10 overflow-x-auto">
        {DOMAIN_TABS.map(d => (
          <button key={d} onClick={() => setTab(d)} data-testid={`iot-tab-${d}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize whitespace-nowrap transition-colors ${tab === d ? 'bg-[#141414] shadow-sm text-white' : 'text-neutral-500 hover:text-white'}`}>
            {d === 'all' ? t('all_devices') : DOMAIN_CONFIG[d]?.label || d}
          </button>
        ))}
      </div>

      {/* Devices by Room */}
      {Object.keys(byRoom).length === 0 ? (
        <div className="text-center py-16 relative z-10">
          <HouseLine size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
          <p className="text-neutral-500 text-sm">{t('no_devices')}</p>
        </div>
      ) : (
        Object.entries(byRoom).sort(([a], [b]) => a.localeCompare(b)).map(([room, roomDevices]) => (
          <div key={room} className="relative z-10">
            <p className="overline mb-3">{room}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {roomDevices.map(device => {
                const config = DOMAIN_CONFIG[device.domain] || DOMAIN_CONFIG.switch;
                const isOn = device.state === 'on' || device.state === 'home' || device.state === 'playing';
                const isSensor = device.domain === 'sensor' || device.domain === 'binary_sensor';
                const Icon = config.icon;
                return (
                  <div key={device.entity_id}
                    className={`glass-card p-4 transition-all duration-200 ${isOn ? 'border-white/20' : ''}`}
                    data-testid={`device-${device.entity_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isOn ? '' : 'opacity-40'}`}
                        style={{ background: `${config.color}15` }}>
                        <Icon size={18} weight="duotone" style={{ color: config.color }} />
                      </div>
                      {!isSensor && (
                        <button onClick={() => handleToggle(device.entity_id)} data-testid={`toggle-${device.entity_id}`}
                          className="transition-transform hover:scale-110">
                          {isOn
                            ? <ToggleRight size={28} weight="fill" className="text-[#22C55E]" />
                            : <ToggleLeft size={28} weight="fill" className="text-neutral-600" />
                          }
                        </button>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-white truncate mb-0.5">{device.name}</p>
                    <p className={`text-[11px] font-medium capitalize ${isOn ? 'text-[#22C55E]' : 'text-neutral-500'}`}>
                      {isSensor ? `${device.state}${device.attributes?.unit_of_measurement ? ' ' + device.attributes.unit_of_measurement : ''}` : device.state}
                    </p>

                    {/* Quick controls for specific domains */}
                    {device.domain === 'light' && isOn && device.attributes?.brightness !== undefined && (
                      <div className="mt-2">
                        <input type="range" min="1" max="255" value={device.attributes.brightness || 128}
                          onChange={(e) => handleAction(device.entity_id, 'turn_on', { brightness: Number(e.target.value) })}
                          className="w-full h-1 rounded-full appearance-none bg-white/10 accent-[#F59E0B]" />
                      </div>
                    )}
                    {device.domain === 'climate' && (
                      <p className="text-[10px] text-neutral-500 mt-1">
                        {device.attributes?.current_temperature && `${device.attributes.current_temperature}°C`}
                        {device.attributes?.temperature && ` / Set: ${device.attributes.temperature}°C`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Create Scene Modal */}
      {showSceneForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowSceneForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('create_scene')}</h3>
            <form onSubmit={handleCreateScene} className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('name')}</label>
                <input type="text" value={sceneForm.name} onChange={e => setSceneForm({ ...sceneForm, name: e.target.value })} required
                  data-testid="scene-name-input" placeholder="e.g., Good Night"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Actions (JSON array)</label>
                <textarea value={JSON.stringify(sceneForm.actions, null, 2)}
                  onChange={e => { try { setSceneForm({ ...sceneForm, actions: JSON.parse(e.target.value) }); } catch {} }}
                  rows={4} data-testid="scene-actions-input"
                  placeholder='[{"entity_id":"light.bedroom","service":"turn_off","data":{}}]'
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-xs font-mono outline-none focus:border-[#FF6B35]/50 resize-none" />
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <input type="text" value={sceneForm.notes} onChange={e => setSceneForm({ ...sceneForm, notes: e.target.value })}
                  data-testid="scene-notes-input" placeholder="Optional"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowSceneForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-scene-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
